import type { RecipeTag } from './RecipeTags';
import type { IngredientGroupInput, RecipeTaxonomyField } from './recipeDetail.util';
import type { RecipeDetail } from './useRecipe';

/**
 * The nine taxonomy fields, in the order their values are laid out as pills. Declared here rather
 * than derived from the query type so the order is deliberate and stable, not an accident of how
 * codegen happened to emit the fields.
 */
const TAXONOMY_FIELDS: RecipeTaxonomyField[] = [
  'cuisine',
  'mealCourse',
  'dietaryRestrictions',
  'healthConsiderations',
  'mainIngredient',
  'preparationTechnique',
  'typeOfDish',
  'styleThemes',
  'seasonOccasion',
];

/**
 * `Recipe.yield` is a free-text String, not a count: the vendor's phrasing isn't guaranteed to be
 * numeric, so the backend stores whatever it was given. Today it is a stringified integer, so this
 * reads it as one and treats anything non-numeric as no stated yield rather than rendering the raw
 * text where a count belongs.
 */
export function parseServings(recipeYield: string | null | undefined): number | null {
  if (recipeYield == null || recipeYield.trim() === '') return null;
  const servings = Number(recipeYield);
  return Number.isFinite(servings) ? servings : null;
}

/** Flattens the nine taxonomy arrays into the flat, field-tagged list `RecipeTags` renders. */
export function toRecipeTags(recipe: RecipeDetail): RecipeTag[] {
  return TAXONOMY_FIELDS.flatMap((field) => recipe[field].map((value) => ({ field, value })));
}

/**
 * Normalizes the query's optional `name` to the explicit `string | null` the ingredient group
 * contract uses, where null is the vendor's unnamed/default group.
 */
export function toIngredientGroups(groups: RecipeDetail['ingredients']): IngredientGroupInput[] {
  return groups.map((group) => ({ name: group.name ?? null, items: group.items }));
}
